import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { signUp, confirmSignUp } from "aws-amplify/auth";

const STEPS = [
  { label: "Account", icon: "🔐" },
  { label: "Personal", icon: "👤" },
  { label: "Address", icon: "📍" },
  { label: "Verify", icon: "✉️" },
];

export default function ProSignUpPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [dob, setDob] = useState("");
  const [phone, setPhone] = useState("");
  const [street, setStreet] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [zip, setZip] = useState("");
  const [code, setCode] = useState("");
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  function validateStep() {
    if (step === 0) {
      if (!username.trim()) return "Username is required";
      if (!email.trim() || !email.includes("@")) return "Valid email is required";
      if (password.length < 8) return "Password must be at least 8 characters";
      if (password !== confirmPassword) return "Passwords do not match";
    }
    if (step === 1) {
      if (!firstName.trim()) return "First name is required";
      if (!lastName.trim()) return "Last name is required";
      if (!dob) return "Date of birth is required";
      const age = (Date.now() - new Date(dob).getTime()) / (1000 * 60 * 60 * 24 * 365);
      if (age < 18) return "You must be at least 18 years old";
      if (!phone.trim()) return "Phone number is required";
    }
    if (step === 2) {
      if (!street.trim()) return "Street address is required";
      if (!city.trim()) return "City is required";
      if (!state.trim()) return "State is required";
      if (!zip.trim() || zip.length < 5) return "Valid zip code is required";
      if (!agreedToTerms) return "Please agree to the Terms of Service and Privacy Policy to continue";
    }
    return "";
  }

  async function handleNext() {
    const err = validateStep();
    if (err) { setError(err); return; }
    setError("");
    if (step === 2) {
      setLoading(true);
      try {
        await signUp({
          username: email,
          password,
          options: {
            userAttributes: {
              email,
              name: `${firstName} ${lastName}`,
              phone_number: phone.startsWith("+") ? phone : `+1${phone.replace(/\D/g, "")}`,
              "custom:role": "pro",
              "custom:username": username,
              "custom:dob": dob,
              "custom:street": street,
              "custom:city": city,
              "custom:state": state,
              "custom:zip": zip,
            }
          }
        });
        setStep(3);
      } catch (e: any) { setError(e.message || "Sign up failed"); }
      finally { setLoading(false); }
      return;
    }
    setStep(step + 1);
  }

  async function handleVerify() {
    if (!code.trim()) { setError("Enter the verification code"); return; }
    setLoading(true); setError("");
    try {
      await confirmSignUp({ username: email, confirmationCode: code });
      navigate("/login?verified=pro");
    } catch (e: any) { setError(e.message || "Verification failed"); }
    finally { setLoading(false); }
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <div className="bg-white border-b border-gray-100 px-5 pt-5 pb-4">
        <button onClick={() => step === 0 ? navigate("/login") : setStep(step - 1)}
          className="text-gray-400 text-sm font-semibold mb-4 flex items-center gap-1">
          ← {step === 0 ? "Back to Login" : "Previous Step"}
        </button>
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-navy rounded-xl flex items-center justify-center text-xl">
            {STEPS[step].icon}
          </div>
          <div>
            <div className="text-xs text-gray-400 font-semibold uppercase tracking-wider">
              Step {step + 1} of {STEPS.length}
            </div>
            <h1 className="font-display text-xl font-bold text-navy">
              {step === 0 ? "Create Your Account" :
               step === 1 ? "Personal Information" :
               step === 2 ? "Business Address" :
               "Verify Email"}
            </h1>
          </div>
        </div>

        {/* Progress bar */}
        <div className="flex gap-1.5">
          {STEPS.map((s, i) => (
            <div key={s.label} className="flex-1">
              <div className={"h-1.5 rounded-full transition-all duration-300 " +
                (i < step ? "bg-mouna-blue" : i === step ? "bg-mouna-blue" : "bg-gray-200")}>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="flex-1 px-5 py-6">
        {error && (
          <div className="bg-red-50 border border-red-100 text-red-500 text-sm rounded-2xl p-3 mb-4 animate-fade-in">
            {error}
          </div>
        )}

        {/* Step 0 - Account */}
        {step === 0 && (
          <div className="animate-slide-up space-y-4">
            <div className="bg-blue-pale rounded-2xl p-4 mb-2">
              <div className="font-bold text-navy text-sm mb-1">🔨 Join as a Professional</div>
              <div className="text-xs text-gray-500">Create your pro account to start receiving leads</div>
            </div>
            <div className="form-section space-y-4">
              <div className="form-group mb-0">
                <label className="label">Username</label>
                <input value={username} onChange={e => setUsername(e.target.value.toLowerCase().replace(/\s/g, ""))}
                  placeholder="e.g. marcus_electric" className="input"/>
              </div>
              <div className="form-group mb-0">
                <label className="label">Email Address</label>
                <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                  placeholder="your@email.com" className="input"/>
              </div>
              <div className="form-group mb-0">
                <label className="label">Password</label>
                <div className="relative">
                  <input type={showPassword ? "text" : "password"} value={password}
                    onChange={e => setPassword(e.target.value)}
                    placeholder="Min 8 characters" className="input pr-12"/>
                  <button type="button" onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 text-sm">
                    {showPassword ? "Hide" : "Show"}
                  </button>
                </div>
              </div>
              <div className="form-group mb-0">
                <label className="label">Confirm Password</label>
                <input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)}
                  placeholder="Repeat password" className="input"/>
              </div>
            </div>
          </div>
        )}

        {/* Step 1 - Personal */}
        {step === 1 && (
          <div className="animate-slide-up space-y-4">
            <div className="form-section space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="form-group mb-0">
                  <label className="label">First Name</label>
                  <input value={firstName} onChange={e => setFirstName(e.target.value)}
                    placeholder="Marcus" className="input"/>
                </div>
                <div className="form-group mb-0">
                  <label className="label">Last Name</label>
                  <input value={lastName} onChange={e => setLastName(e.target.value)}
                    placeholder="Thorne" className="input"/>
                </div>
              </div>
              <div className="form-group mb-0">
                <label className="label">Date of Birth</label>
                <input type="date" value={dob} onChange={e => setDob(e.target.value)}
                  max={new Date(Date.now() - 18 * 365.25 * 24 * 60 * 60 * 1000).toISOString().split("T")[0]}
                  className="input"/>
                <div className="text-xs text-gray-400 mt-1">Must be 18 or older</div>
              </div>
              <div className="form-group mb-0">
                <label className="label">Phone Number</label>
                <input type="tel" value={phone} onChange={e => setPhone(e.target.value)}
                  placeholder="+1 (832) 000-0000" className="input"/>
              </div>
            </div>
          </div>
        )}

        {/* Step 2 - Address */}
        {step === 2 && (
          <div className="animate-slide-up space-y-4">
            <div className="bg-yellow-50 border border-yellow-100 rounded-2xl p-4 mb-2">
              <div className="text-xs text-yellow-700">
                <span className="font-bold">📌 Important:</span> Your address determines your service area and lead eligibility.
              </div>
            </div>
            <div className="form-section space-y-4">
              <div className="form-group mb-0">
                <label className="label">Street Address</label>
                <input value={street} onChange={e => setStreet(e.target.value)}
                  placeholder="123 Main St" className="input"/>
              </div>
              <div className="form-group mb-0">
                <label className="label">City</label>
                <input value={city} onChange={e => setCity(e.target.value)}
                  placeholder="Houston" className="input"/>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="form-group mb-0">
                  <label className="label">State</label>
                  <input value={state} onChange={e => setState(e.target.value)}
                    placeholder="TX" maxLength={2} className="input"/>
                </div>
                <div className="form-group mb-0">
                  <label className="label">Zip Code</label>
                  <input value={zip} onChange={e => setZip(e.target.value.replace(/\D/g, "").slice(0, 5))}
                    placeholder="77001" className="input"/>
                </div>
              </div>
              <label className="flex items-start gap-3 mt-4 cursor-pointer">
                <input type="checkbox" checked={agreedToTerms} onChange={e => setAgreedToTerms(e.target.checked)}
                  className="mt-0.5 w-4 h-4 accent-mouna-blue flex-shrink-0"/>
                <span className="text-xs text-gray-500 leading-relaxed">
                  I agree to the{" "}
                  <Link to="/terms" className="text-mouna-blue underline">Terms of Service</Link>{" "}
                  and{" "}
                  <Link to="/privacy" className="text-mouna-blue underline">Privacy Policy</Link>
                </span>
              </label>
            </div>
          </div>
        )}

        {/* Step 3 - Verify */}
        {step === 3 && (
          <div className="animate-slide-up text-center">
            <div className="w-20 h-20 bg-blue-pale rounded-full flex items-center justify-center text-4xl mx-auto mb-4">✉️</div>
            <h2 className="font-bold text-navy text-lg mb-2">Check your email</h2>
            <p className="text-sm text-gray-500 mb-6">
              We sent a 6-digit code to<br/>
              <span className="font-bold text-navy">{email}</span>
            </p>
            <div className="form-group">
              <input value={code} onChange={e => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="000000"
                className="input text-center text-3xl tracking-widest font-bold py-4"/>
            </div>
            <div className="text-xs text-gray-400 mt-2">
              Didn't receive it?{" "}
              <button className="text-mouna-blue font-bold">Resend code</button>
            </div>
          </div>
        )}
      </div>

      {/* Bottom button */}
      <div className="px-5 pb-8 pt-4 bg-white border-t border-gray-100">
        <button onClick={step === 3 ? handleVerify : handleNext} disabled={loading}
          className="btn btn-primary btn-full btn-lg">
          {loading ? "Please wait..." :
           step === 3 ? "Verify & Go Live 🚀" :
           step === 2 ? "Create Account" :
           `Continue → Step ${step + 2} of ${STEPS.length}`}
        </button>
        {step === 0 && (
          <div className="text-center mt-3 text-sm text-gray-500">
            Already have an account?{" "}
            <button onClick={() => navigate("/login")} className="text-mouna-blue font-bold">Sign in</button>
          </div>
        )}
      </div>
    </div>
  );
}
